import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Icon } from '@iconify/react';
import Editor from "@monaco-editor/react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppStore } from "../zustand/useAppStore";
import { showConfirm } from '../components/showConfirm'; // 🌟 Confirm Dialog Import
import { axiosInstance } from '../services/axios';

const defaultTemplates = {
  form: {
    title: "اضافة كورس",
    message: "اضافة كورس جديد",
    submit: "اضافة",
    req: "post",
    onSuccess: "ClearAll(); CloseModal(); RefreshMainData();",
    onFailed: "",
    js: "",
    data: [] 
  },
  table: {
    th: "<tr style={{background:`steelblue`, color:`white`}}> <th>...</th> </tr>",
    category: "table",
    limit: "50",
    search: true,
    apiData: "",
    placeholder: "أبحث...",
    data: []
  },
  list: {
    category: "list",
    limit: "50",
    search: true,
    apiData: "",
    placeholder: "أبحث...",
    data: []
  },
  custom: {
    fetchingdata: [],
    source: "const [isSubmited, setIsSubmited] = React.useState(false);\n...\nreturn(<div className='mb-[150px]'>...</div>)",
  }
};

const defaultArrayItemTemplates = {
  form: { className: "input", label: "", id: "", name: "", value: "", type: "text", required: "1" },
  table: { data: "<div>...</div>", items: "{\n  \"type\": \"form\",\n  \"api\": \"...\"\n}" },
  list: { data: "<div>...</div>", items: "{\n  \"type\": \"form\",\n  \"api\": \"...\"\n}" }, 
  custom: { id: "", api: "", apiData: "", value: "" }
};

const getEmptyArrayItemFromTemplate = (type, templatesMap) => {
  const template = templatesMap[type] || defaultArrayItemTemplates[type];
  const emptyItem = {};
  if (template) {
    Object.keys(template).forEach(key => {
      emptyItem[key] = "";
    });
  }
  return emptyItem;
};

const getEmptyDetails = (type) => {
  const template = defaultTemplates[type];
  const emptyDetails = {};
  if (template) {
    Object.keys(template).forEach(key => {
      if (Array.isArray(template[key])) {
        emptyDetails[key] = []; 
      } else if (typeof template[key] === 'boolean') {
        emptyDetails[key] = template[key]; 
      } else {
        emptyDetails[key] = ""; 
      }
    });
  }
  return emptyDetails;
};

export default function ERPComponentBuilder({ componentToUpdate }) {
  const { themeMode } = useAppStore();
  const isDarkMode = themeMode === 'dark';

  const [arrayTemplates, setArrayTemplates] = useState(defaultArrayItemTemplates);
  
  // 🌟 SNAPSHOT STATE: Keeps the original data intact so we can revert back to it
  const [originalComponent, setOriginalComponent] = useState(null);

  const [component, setComponent] = useState({
    id: "",
    type: "form",
    api: "",
    details: getEmptyDetails("form")
  });

  const isPlainObject = (param) => {
    return param !== null && typeof param === 'object' && !Array.isArray(param);
  };

  useEffect(() => {
    if (isPlainObject(componentToUpdate)) {
      
      let safeDetails = componentToUpdate.details;
      
      try {
        while (typeof safeDetails === 'string' && safeDetails.trim() !== "") {
          safeDetails = JSON.parse(safeDetails);
        }
      } catch (error) {
        console.error("Failed to parse details from DB", error);
        safeDetails = null;
      }

      const loadedType = componentToUpdate.type || "form";

      if (!isPlainObject(safeDetails)) {
        safeDetails = getEmptyDetails(loadedType);
      }

      const arrayName = loadedType === 'custom' ? 'fetchingdata' : 'data';
      const loadedArray = safeDetails[arrayName];

      let updatedTemplates = { ...defaultArrayItemTemplates };

      if (Array.isArray(loadedArray) && loadedArray.length > 0) {
        const mergedKeys = { ...(defaultArrayItemTemplates[loadedType] || {}) };
        
        loadedArray.forEach(item => {
          if (isPlainObject(item)) {
            Object.keys(item).forEach(key => {
              if (!(key in mergedKeys)) {
                mergedKeys[key] = ""; 
              }
            });
          }
        });
        
        updatedTemplates[loadedType] = mergedKeys;
      }

      setArrayTemplates(updatedTemplates);

      const loadedComponentState = {
        id: componentToUpdate.sys_components_id || "",
        type: loadedType,
        api: componentToUpdate.api || "",
        details: safeDetails
      };

      setComponent(loadedComponentState);
      setOriginalComponent(loadedComponentState); 
      
      setNewArrayItem(getEmptyArrayItemFromTemplate(loadedType, updatedTemplates));
    }
  }, [componentToUpdate]);

  const [isSubmited, setIsSubmited] = React.useState(false);
  const [newArrayItem, setNewArrayItem] = useState(getEmptyArrayItemFromTemplate("form", defaultArrayItemTemplates));
  const [customItemKey, setCustomItemKey] = useState('');
  const [editIndex, setEditIndex] = useState(null);
  const [newDetailKey, setNewDetailKey] = useState('');
  const [newDetailValue, setNewDetailValue] = useState('');

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [tempCode, setTempCode] = useState('');
  const [editorConfig, setEditorConfig] = useState({
    target: null,
    key: '',
    language: 'javascript'
  });

  const openEditor = (target, key, currentValue) => {
    let lang = 'javascript';
    if (key === 'items' || typeof currentValue === 'object') lang = 'json';
    if (key === 'th' || key === 'html' || key === 'data') lang = 'html';

    setEditorConfig({ target, key, language: lang });
    const stringValue = typeof currentValue === 'object' ? JSON.stringify(currentValue, null, 2) : currentValue;
    setTempCode(stringValue || "");
    setIsEditorOpen(true);
  };

  const handleEditorSave = () => {
    if (editorConfig.target === 'detail') {
      handleDetailsChange(editorConfig.key, tempCode);
    } else if (editorConfig.target === 'arrayItem') {
      handleArrayItemChange(editorConfig.key, tempCode);
    }
    setIsEditorOpen(false);
  };

  const Submit = async (e) => {
    e.preventDefault();
    if (!isSubmited) {
      setIsSubmited(true);
      
      const payloadToSend = {
        ...component,
        details: JSON.stringify(component.details)
      };
      const res = await axiosInstance.put('RoutesComponents.php', payloadToSend);
      try {
        if (res.data.success) {
          toast.success(res.data.msg);
        } else {
          toast.error(res.data.msg);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsSubmited(false);  
      }
    }
  };

  const handleMainChange = (e) => {
    const { name, value } = e.target;
    if (name === 'type') {
      
      if (originalComponent) {
        showConfirm({
          title: 'تأكيد تغيير نوع المكون',
          message: 'هل أنت متأكد؟ سيؤدي تغيير نوع المكون إلى مسح وإعادة ضبط كافة التفاصيل والمصفوفات الحالية.',
          type: 'warning',
          onConfirm: () => {
            setComponent({
              ...component,
              type: value,
              details: getEmptyDetails(value) 
            });
            setNewArrayItem(getEmptyArrayItemFromTemplate(value, arrayTemplates)); 
            setEditIndex(null);
            toast.success("تم تغيير النوع بنجاح وإعادة ضبط البيانات");
          }
        });
      } else {
        setComponent({
          ...component,
          type: value,
          details: getEmptyDetails(value) 
        });
        setNewArrayItem(getEmptyArrayItemFromTemplate(value, arrayTemplates)); 
        setEditIndex(null); 
      }
      
    } else {
      setComponent({ ...component, [name]: name === 'id' ? Number(value) : value });
    }
  };

  const revertToOriginal = () => {
    if (originalComponent) {
      setComponent(originalComponent);
      setNewArrayItem(getEmptyArrayItemFromTemplate(originalComponent.type, arrayTemplates));
      setEditIndex(null);
      toast.success("تم التراجع واستعادة بيانات المكون الأصلية");
    }
  };

  const handleDetailsChange = (key, value) => {
    setComponent((prev) => ({
      ...prev,
      details: { ...prev.details, [key]: value }
    }));
  };

  const addCustomDetailConfig = () => {
    if (!newDetailKey.trim()) return alert("Please enter a key name");
    let parsedValue = newDetailValue;
    if (newDetailValue === 'true') parsedValue = true;
    if (newDetailValue === 'false') parsedValue = false;
    
    setComponent((prev) => ({
      ...prev,
      details: { ...prev.details, [newDetailKey]: parsedValue }
    }));
    setNewDetailKey('');
    setNewDetailValue('');
  };

  const removeDetailConfig = (key) => {
    const updatedDetails = { ...component.details };
    delete updatedDetails[key];
    setComponent({ ...component, details: updatedDetails });
  };

  const handleArrayItemChange = (key, value) => {
    setNewArrayItem({ ...newArrayItem, [key]: value });
  };

  const addCustomKeyToArrayItem = () => {
    if (!customItemKey.trim()) return;

    setNewArrayItem(prev => ({ ...prev, [customItemKey]: "" }));
    
    setArrayTemplates(prev => ({
      ...prev,
      [component.type]: {
        ...(prev[component.type] || {}),
        [customItemKey]: ""
      }
    }));

    setCustomItemKey('');
  };

  const removeKeyFromArrayItem = (keyToRemove) => {
    const updatedItem = { ...newArrayItem };
    delete updatedItem[keyToRemove];
    setNewArrayItem(updatedItem);
  };

  const loadItemForEdit = (index) => {
    const arrayName = component.type === 'custom' ? 'fetchingdata' : 'data';
    const itemToEdit = { ...component.details[arrayName][index] };

    if ((component.type === 'table' || component.type === 'list') && typeof itemToEdit.items === 'object') {
      itemToEdit.items = JSON.stringify(itemToEdit.items, null, 2);
    }

    setNewArrayItem(itemToEdit);
    setEditIndex(index); 
  };

  const cancelEdit = () => {
    setNewArrayItem(getEmptyArrayItemFromTemplate(component.type, arrayTemplates));
    setEditIndex(null);
  };

  const pushItemToArray = () => {
    const arrayName = component.type === 'custom' ? 'fetchingdata' : 'data';
    let processedItem = { ...newArrayItem };

    if ((component.type === 'table' || component.type === 'list') && processedItem.items !== undefined) {
      try {
        if (typeof processedItem.items === 'string' && processedItem.items.trim() !== '') {
            processedItem.items = JSON.parse(processedItem.items);
        }
      } catch (e) {
        alert("خطأ في صيغة JSON في حقل (items). الرجاء التأكد من كتابتها بشكل صحيح.");
        return; 
      }
    }
    
    setComponent((prev) => {
      const currentArray = prev.details[arrayName] || [];
      let newArray;

      if (editIndex !== null) {
        newArray = [...currentArray];
        newArray[editIndex] = processedItem;
      } else {
        newArray = [...currentArray, processedItem];
      }

      return {
        ...prev,
        details: { 
          ...prev.details, 
          [arrayName]: newArray 
        }
      };
    });

    setNewArrayItem(getEmptyArrayItemFromTemplate(component.type, arrayTemplates));
    setEditIndex(null);
  };

  const removeFromArray = (index) => {
    const arrayName = component.type === 'custom' ? 'fetchingdata' : 'data';
    const updatedArray = [...component.details[arrayName]];
    updatedArray.splice(index, 1);

    setComponent((prev) => ({
      ...prev,
      details: { ...prev.details, [arrayName]: updatedArray }
    }));
    
    if (editIndex === index) {
      cancelEdit();
    }
  };

  const currentArrayName = component.type === 'custom' ? 'fetchingdata' : 'data';
  const currentArray = component.details[currentArrayName] || [];

  const hasChanges = originalComponent && JSON.stringify(component) !== JSON.stringify(originalComponent);

  return (
    // 🌟 Notice the h-[calc(100vh-80px)] and overflow-hidden to stop main page scrolling
    // (Adjust the 80px value if your top navbar height is different!)
    <div className="flex flex-col lg:flex-row w-full h-[calc(100vh-80px)] bg-bg-primary text-text-primary p-4 md:p-6 gap-6 transition-colors duration-300 overflow-hidden" dir="rtl">
      
      {/* 🌟 FULL SCREEN MONACO EDITOR OVERLAY */}
      <AnimatePresence>
        {isEditorOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] flex flex-col bg-bg-primary"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-bg-secondary shadow-sm">
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setIsEditorOpen(false)}
                  className="p-1 bg-bg-tertiary border border-border text-text-primary font-bold rounded-lg hover:bg-bg-primary transition-all flex items-center gap-1"
                >
                  <Icon icon="solar:alt-arrow-right-linear" className="text-lg" />
                </button>
                <Icon icon="solar:code-square-bold-duotone" className="text-3xl text-brand-primary" />
                <div>
                  <h2 className="text-lg font-black text-text-primary">محرر الأكواد المتقدم</h2>
                  <p className="text-xs text-text-secondary font-mono" dir="ltr">Key: {editorConfig.key} | Lang: {editorConfig.language}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button 
                  onClick={handleEditorSave}
                  className="p-2 md:px-6 md:py-2.5 bg-brand-primary text-white font-bold rounded-xl hover:bg-brand-hover shadow-lg transition-all flex items-center gap-2"
                >
                  <Icon icon="solar:diskette-bold-duotone" className="text-xl" />
                  <span className='hidden md:block'>حفظ</span> 
                </button>
              </div>
            </div>
            
            <div className="flex-1 w-full h-full relative" dir="ltr">
              <Editor
                height="100%"
                width="100%"
                language={editorConfig.language}
                theme={isDarkMode ? "vs-dark" : "light"}
                value={tempCode}
                onChange={(value) => setTempCode(value)}
                options={{
                  minimap: { enabled: true, scale: 0.75 },
                  fontSize: 15,
                  fontFamily: "'Fira Code', 'Consolas', monospace",
                  fontLigatures: true,
                  automaticLayout: true,
                  wordWrap: "on",
                  tabSize: 2,
                  smoothScrolling: true,
                  cursorBlinking: "smooth",
                  scrollBeyondLastLine: false,
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* LEFT COLUMN: THE BUILDER FORM (Scrollable independently) */}
      <div className="w-full lg:w-1/2 flex flex-col gap-6 h-full overflow-y-auto custom-scrollbar pr-2 pb-10">
        
        {/* Header & Submit Button */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-bg-primary p-4 rounded-2xl shadow-sm border border-border shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand-light rounded-xl">
              <Icon icon="solar:widget-add-bold-duotone" className="text-brand-primary text-2xl" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-black text-text-primary">محرر المكونات</h1>
              {originalComponent && (
                <p className="text-xs text-status-warning font-bold mt-1">تحديث المكون {originalComponent.id}</p>
              )}
            </div>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            {hasChanges && (
              <button 
                onClick={revertToOriginal} 
                className="flex-1 sm:flex-none bg-bg-tertiary border border-border hover:bg-bg-secondary text-text-primary font-bold py-2.5 px-4 rounded-xl transition-all shadow-sm flex items-center justify-center gap-2"
                title="تراجع عن كافة التغييرات"
              >
                <Icon icon="solar:undo-left-bold-duotone" className="text-xl text-status-warning" />
                <span className="hidden xl:block">استعادة الأصل</span>
              </button>
            )}
            <button 
              onClick={Submit} 
              disabled={isSubmited}
              className="flex-1 sm:flex-none bg-brand-primary hidden lg:flex hover:bg-brand-hover text-white font-bold py-2.5 px-6 rounded-xl transition-all shadow-md items-center justify-center gap-2 disabled:opacity-70"
            >
              <Icon icon="solar:diskette-bold-duotone" className="text-xl" />
              {isSubmited ? 'جاري التحديث...' : 'حفظ التحديثات'}
            </button>
          </div>
        </div>

        {/* 1. Main Config */}
        <div className="bg-bg-primary p-5 md:p-6 rounded-2xl shadow-sm border border-border shrink-0">
          <h2 className="text-lg font-bold mb-4 text-brand-primary border-b border-border pb-2">1. الإعدادات الأساسية (Main Config)</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-xl bg-bg-secondary border border-border">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-bold text-text-secondary px-1">Component ID</label>
              <input 
                type="number" 
                name="id" 
                placeholder="مثال: 1" 
                value={component.id} 
                onChange={handleMainChange} 
                readOnly={!!originalComponent}
                className={`w-full p-2.5 border border-border rounded-xl focus:outline-none focus:border-brand-primary text-text-primary placeholder-text-secondary/50 transition-all ${
                  originalComponent ? 'bg-bg-secondary opacity-60 cursor-not-allowed' : 'bg-bg-tertiary'
                }`} 
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-bold text-text-primary px-1">Type</label>
              <select name="type" value={component.type} onChange={handleMainChange} className="w-full p-2.5 bg-bg-tertiary text-text-primary border border-border rounded-xl text-brand-primary font-bold focus:outline-none focus:border-brand-primary">
                <option className='text-text-primary' value="form">form</option>
                <option className='text-text-primary' value="table">table</option>
                <option className='text-text-primary' value="list">list</option>
                <option className='text-text-primary' value="custom">custom</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-bold text-text-secondary px-1">API</label>
              <input type="text" name="api" placeholder="مثال: Services.php" value={component.api} onChange={handleMainChange} className="w-full p-2.5 bg-bg-tertiary border border-border rounded-xl focus:outline-none focus:border-brand-primary text-text-primary placeholder-text-secondary/50" />
            </div>
          </div>
        </div>

        {/* 2. Details Config */}
        <div className="bg-bg-primary p-5 md:p-6 rounded-2xl shadow-sm border border-border shrink-0">
          <h3 className="text-lg font-bold mb-4 text-brand-primary border-b border-border pb-2">2. تفاصيل المكون (Details)</h3>
          <div className="space-y-4 mb-6">
            {Object.entries(component.details).map(([key, value]) => {
              if (Array.isArray(value)) return null; 

              const defaultPlaceholder = defaultTemplates[component.type]?.[key] || `أدخل ${key}...`;

              return (
                <div key={key} className="flex flex-col sm:flex-row sm:items-start gap-2 bg-bg-secondary p-3 rounded-xl border border-border">
                  <label className="w-full sm:w-1/4 font-bold text-sm text-text-secondary mt-2">{key}:</label>
                  
                  {typeof value === 'boolean' ? (
                    <select value={value} onChange={(e) => handleDetailsChange(key, e.target.value === 'true')} className="w-full sm:flex-1 p-2.5 border border-border rounded-lg bg-bg-tertiary text-text-primary focus:border-brand-primary focus:outline-none">
                      <option value={true}>True</option>
                      <option value={false}>False</option>
                    </select>
                  ) : (
                    <div className="flex gap-2 w-full sm:flex-1 relative">
                      <textarea 
                        dir="ltr" 
                        value={value} 
                        onChange={(e) => handleDetailsChange(key, e.target.value)} 
                        placeholder={defaultPlaceholder} 
                        className="w-full p-2.5 border border-border rounded-lg bg-bg-tertiary text-text-primary h-12 min-h-[48px] focus:border-brand-primary focus:outline-none font-mono text-sm placeholder-text-secondary/50 custom-scrollbar" 
                      />
                      <button 
                        onClick={() => openEditor('detail', key, value)}
                        className="shrink-0 w-12 flex items-center justify-center bg-brand-primary/10 text-brand-primary border border-brand-primary/30 rounded-lg hover:bg-brand-primary hover:text-white transition-all shadow-sm"
                        title="فتح في محرر الأكواد"
                      >
                        <Icon icon="solar:code-square-bold-duotone" className="text-2xl" />
                      </button>
                    </div>
                  )}

                  <button onClick={() => removeDetailConfig(key)} className="w-full sm:w-auto px-3 py-2.5 bg-status-danger/10 text-status-danger rounded-lg hover:bg-status-danger hover:text-white transition-colors" title="حذف">
                    <Icon icon="solar:trash-bin-trash-bold-duotone" className="text-lg mx-auto" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="p-4 border border-brand-primary/30 rounded-xl bg-bg-secondary">
            <h4 className="text-sm font-bold text-brand-primary mb-3">إضافة خاصية جديدة:</h4>
            <div className="flex flex-col sm:flex-row gap-2">
              <input type="text" placeholder="مفتاح جديد (Key)" value={newDetailKey} onChange={(e) => setNewDetailKey(e.target.value)} className="w-full sm:w-1/3 p-2.5 bg-bg-tertiary border border-border rounded-lg focus:border-brand-primary focus:outline-none text-text-primary" />
              <textarea dir='auto' placeholder="القيمة (Value)" value={newDetailValue} onChange={(e) => setNewDetailValue(e.target.value)} className="w-full sm:flex-1 p-2.5 bg-bg-tertiary border border-border rounded-lg focus:border-brand-primary focus:outline-none text-text-primary h-11" />
              <button onClick={addCustomDetailConfig} className="w-full sm:w-auto bg-brand-primary text-white px-4 py-2.5 rounded-lg hover:bg-brand-hover transition-colors font-bold">إضافة</button>
            </div>
          </div>
        </div>

        {/* 3. ARRAY BUILDER */}
        <div className={`bg-bg-primary p-5 md:p-6 rounded-2xl shadow-sm transition-all duration-300 shrink-0 ${editIndex !== null ? 'border-2 border-status-warning shadow-status-warning/20' : 'border border-border'}`}>
          <h3 className={`text-lg font-bold mb-4 border-b border-border pb-2 flex items-center gap-2 ${editIndex !== null ? 'text-status-warning' : 'text-brand-primary'}`}>
            3. بناء المصفوفة ({currentArrayName})
            {editIndex !== null && <span className="text-xs bg-status-warning/20 text-status-warning px-2 py-1 rounded-md">وضع التعديل</span>}
          </h3>
          
          <div className={`grid ${component.type === 'table' || component.type === 'list' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-4`}>
            {Object.keys(newArrayItem).map((key) => {
              const defaultPlaceholder = arrayTemplates[component.type]?.[key] || defaultArrayItemTemplates[component.type]?.[key] || `أدخل ${key}...`;

              return (
                <div key={key} className="flex flex-col bg-bg-secondary p-3 rounded-lg border border-border relative group">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-xs font-bold text-text-secondary">{key}</label>
                    <button 
                      onClick={() => removeKeyFromArrayItem(key)}
                      className="text-status-danger hover:bg-status-danger/10 rounded-md p-1 transition-colors"
                      title="حذف هذا المفتاح"
                    >
                      <Icon icon="solar:close-circle-bold" className="text-lg" />
                    </button>
                  </div>

                  {(key === 'data' || key === 'items') ? (
                    <div className="relative flex">
                      <textarea 
                        value={typeof newArrayItem[key] === 'object' ? JSON.stringify(newArrayItem[key], null, 2) : newArrayItem[key]} 
                        onChange={(e) => handleArrayItemChange(key, e.target.value)}
                        placeholder={defaultPlaceholder}
                        className="w-full p-3 bg-bg-tertiary border border-border rounded-md text-sm font-mono text-text-primary focus:border-brand-primary focus:outline-none placeholder-text-secondary/50 custom-scrollbar pr-12"
                        rows={key === 'items' ? 6 : 4}
                        dir="ltr"
                      />
                      <button 
                        onClick={() => openEditor('arrayItem', key, newArrayItem[key])}
                        className="absolute right-2 top-2 p-2 bg-brand-primary/10 text-brand-primary border border-brand-primary/30 rounded-lg hover:bg-brand-primary hover:text-white transition-all shadow-sm"
                        title="فتح في محرر الأكواد"
                      >
                        <Icon icon="solar:code-square-bold-duotone" className="text-xl" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative flex">
                      <input 
                        type="text" 
                        value={newArrayItem[key]} 
                        onChange={(e) => handleArrayItemChange(key, e.target.value)}
                        placeholder={defaultPlaceholder}
                        className="w-full p-2.5 bg-bg-tertiary border border-border rounded-md text-sm text-text-primary focus:border-brand-primary focus:outline-none placeholder-text-secondary/50 pr-12"
                        dir="ltr"
                      />
                      <button 
                        onClick={() => openEditor('arrayItem', key, newArrayItem[key])}
                        className="absolute right-1.5 top-1.5 p-1.5 bg-brand-primary/10 text-brand-primary border border-brand-primary/30 rounded-md hover:bg-brand-primary hover:text-white transition-all shadow-sm"
                        title="فتح في محرر الأكواد"
                      >
                        <Icon icon="solar:code-square-bold-duotone" className="text-sm" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex flex-col xl:flex-row gap-4 my-4 items-start xl:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-2 w-full xl:w-1/2">
              <input 
                type="text" 
                placeholder="إضافة مفتاح جديد (Key)" 
                value={customItemKey}
                onChange={(e) => setCustomItemKey(e.target.value)}
                className="w-full sm:flex-1 p-2.5 bg-bg-secondary border border-border rounded-lg text-sm text-text-primary focus:border-brand-primary focus:outline-none"
              />
              <button onClick={addCustomKeyToArrayItem} className="w-full sm:w-auto bg-bg-secondary border border-border text-text-primary px-4 py-2.5 rounded-lg text-sm font-bold hover:bg-border transition-colors">
                + إضافة
              </button>
            </div>
            
            <div className="flex gap-2 w-full xl:w-auto">
              {editIndex !== null && (
                <button onClick={cancelEdit} className="flex-1 xl:flex-none bg-bg-secondary border border-border text-text-secondary px-6 py-2.5 rounded-xl font-bold hover:bg-border transition-colors">
                  إلغاء
                </button>
              )}
              <button onClick={pushItemToArray} className={`flex-1 xl:flex-none text-white px-6 py-2.5 rounded-xl font-bold shadow-md transition-colors flex items-center justify-center gap-2 ${editIndex !== null ? 'bg-status-warning hover:bg-yellow-600' : 'bg-status-success hover:bg-emerald-600'}`}>
                <Icon icon={editIndex !== null ? "solar:pen-bold-duotone" : "solar:add-circle-bold-duotone"} className="text-xl" />
                {editIndex !== null ? 'تحديث العنصر' : 'إضافة للمصفوفة'}
              </button>
            </div>
          </div>

          {currentArray.length > 0 && (
            <div className="mt-8 border-t border-border pt-6">
              <h4 className="font-bold mb-4 text-text-secondary">العناصر المضافة ({currentArray.length}):</h4>
              <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                {currentArray.map((item, index) => (
                  <div key={index} className={`flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 rounded-xl border gap-4 transition-colors ${editIndex === index ? 'bg-status-warning/10 border-status-warning' : 'bg-bg-secondary border-border hover:border-brand-primary'}`}>
                    <pre className="flex-1 w-full overflow-x-auto whitespace-pre-wrap text-xs bg-bg-primary p-3 rounded-lg border border-border font-mono text-text-primary" dir="ltr">
                      {JSON.stringify(item, null, 2)}
                    </pre>

                    <div className="flex gap-2 w-full sm:w-auto shrink-0">
                      <button onClick={() => loadItemForEdit(index)} className="flex-1 sm:flex-none p-2.5 bg-status-warning/10 text-status-warning hover:bg-status-warning hover:text-white rounded-lg transition-colors">
                        <Icon icon="solar:pen-bold-duotone" className="text-xl mx-auto" />
                      </button>
                      <button onClick={() => removeFromArray(index)} className="flex-1 sm:flex-none p-2.5 bg-status-danger/10 text-status-danger hover:bg-status-danger hover:text-white rounded-lg transition-colors">
                        <Icon icon="solar:trash-bin-trash-bold-duotone" className="text-xl mx-auto" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Mobile Submit Button (Shows at the bottom of the left column when on small screens) */}
        <div className="lg:hidden shrink-0 pt-4">
          <button 
            onClick={Submit} 
            disabled={isSubmited}
            className="w-full bg-brand-primary hover:bg-brand-hover text-white font-bold py-2.5 px-6 rounded-xl transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-70"
          >
            <Icon icon="solar:diskette-bold-duotone" className="text-xl" />
            {isSubmited ? 'جاري التحديث...' : 'حفظ التحديثات'}
          </button>
        </div>

      </div>

      {/* RIGHT COLUMN: LIVE JSON OUTPUT (Fixed/Sticky) */}
      <div className="hidden lg:flex w-full lg:w-1/2 h-full flex-col shrink-0">
        <div className="bg-slate-900 rounded-3xl p-5 md:p-6 shadow-xl border border-slate-700 flex flex-col h-full" dir="ltr">
          <div className="flex items-center gap-2 mb-4 border-b border-slate-700 pb-4 shrink-0">
            <Icon icon="solar:code-square-bold-duotone" className="text-emerald-400 text-2xl" />
            <h2 className="text-white text-lg font-bold">Live JSON Output</h2>
          </div>
          <div className="flex-1 overflow-auto bg-slate-950 p-4 rounded-xl border border-slate-800 custom-scrollbar">
            <pre className="whitespace-pre-wrap text-xs md:text-sm font-mono text-emerald-400">
              {JSON.stringify(component, null, 2)}
            </pre>
          </div>
        </div>
      </div>

    </div>
  );
}